import { getApiBaseUrl } from '@/lib/constants/api';
import { academyPath } from '@/lib/schema/paths';

/**
 * Grouped here (not lib/auth/providers/) because these are browser-side URL builders,
 * unlike the server-only provider modules they used to sit beside. Each must match an IdP's
 * registered value byte-for-byte.
 */

/** OIDC redirect_uri, shared by the authorize and callback legs so they can't drift apart. */
export function getOAuthRedirectUri(): string {
  if (process.env.NEXT_PUBLIC_OAUTH_CLIENT_URL) {
    // Must be the app root (e.g. https://…/academy).
    return process.env.NEXT_PUBLIC_OAUTH_CLIENT_URL;
  }
  if (typeof window === 'undefined') return '';
  // App root, not /login — no such route exists; <SsoCallbackHandler/> on the landing
  // page handles the /academy?code=… callback.
  return `${window.location.origin}${academyPath('')}`;
}

/**
 * "Log in with Contentstack SSO". The Academy API owns the OIDC handshake; this just sends
 * the browser there. See providers/sso.ts for the return leg.
 */
export function getContentstackAuthorizeUrl(region: string, redirectUri: string): string {
  return `${getApiBaseUrl()}/v1/auth/contentstack?region=${region}&redirect_uri=${encodeURIComponent(redirectUri)}`;
}

/**
 * "Log In As Partner". The API owns the whole round trip (redirect to Impartner, ACS,
 * session cookie) and sends the browser back already logged in — nothing to exchange here,
 * so SAML has no provider module or route handler; PartnerSessionSync just adopts the
 * landed session.
 *
 * `returnTo` keeps the /academy prefix — the API resolves it against its own appUrl. The API
 * validates it and only passes an opaque id to the IdP, so it can't become an open redirect.
 */
export function getSamlLoginUrl(returnTo?: string): string {
  const base = `${getApiBaseUrl()}/v1/saml/login`;
  return returnTo ? `${base}?returnTo=${encodeURIComponent(returnTo)}` : base;
}
